<template>
    <div
        class="follower-stats-display"
        :style="{
            color: appearance?.fonts_color,
            fontFamily: appearance?.fonts,
        }"
    >
        <button
            type="button"
            class="follower-stats-display__total"
            :class="{
                'follower-stats-display__total--clickable':
                    visibleAccounts.length,
            }"
            @click="openSheet"
        >
            <span class="follower-stats-display__count">
                {{ formatNumber(totalFollowers) }}
            </span>
            <span class="follower-stats-display__label">Total Followers</span>
        </button>

        <Teleport to="body">
            <transition name="follower-stats-sheet">
                <div
                    v-if="isSheetOpen"
                    class="follower-stats-sheet"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="follower-stats-sheet-title"
                    @click.self="closeSheet"
                >
                    <div
                        class="follower-stats-sheet__panel"
                        :style="sheetStyle"
                    >
                        <div class="follower-stats-sheet__handle"></div>
                        <div class="follower-stats-sheet__header">
                            <h2
                                id="follower-stats-sheet-title"
                                class="follower-stats-sheet__title"
                            >
                                <span>{{ formatNumber(totalFollowers) }}</span>
                                Total Followers
                            </h2>
                            <button
                                type="button"
                                class="follower-stats-sheet__close"
                                aria-label="Close follower breakdown"
                                @click="closeSheet"
                            >
                                <IconX
                                    :size="18"
                                    :stroke-width="2.4"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>

                        <div class="follower-stats-display__accounts">
                            <div
                                v-for="account in visibleAccounts"
                                :key="`${account.source}-${account.id}`"
                                class="follower-stats-display__account"
                            >
                                <div
                                    class="follower-stats-display__avatar-wrap"
                                >
                                    <img
                                        v-if="account.avatar_url"
                                        :src="account.avatar_url"
                                        alt=""
                                        class="follower-stats-display__avatar"
                                        loading="lazy"
                                    />
                                    <span
                                        v-else
                                        class="follower-stats-display__avatar follower-stats-display__avatar--fallback"
                                        v-html="platformIcon(account.platform)"
                                    ></span>
                                    <span
                                        v-if="account.avatar_url"
                                        class="follower-stats-display__badge"
                                        v-html="platformIcon(account.platform)"
                                    ></span>
                                </div>
                                <div
                                    class="follower-stats-display__account-copy"
                                >
                                    <span
                                        class="follower-stats-display__account-name"
                                    >
                                        {{
                                            account.account ||
                                            account.platform_label
                                        }}
                                    </span>
                                    <span
                                        class="follower-stats-display__account-count"
                                    >
                                        {{
                                            formatCompactNumber(
                                                account.follower_count
                                            )
                                        }}
                                        {{
                                            account.count_label || "Followers"
                                        }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script>
import { getSvgForPlatform } from "@/services/iconService.js";
import { IconX } from "@tabler/icons-vue";

export default {
    name: "FollowerStatsDisplay",
    components: {
        IconX,
    },
    props: {
        totalFollowers: {
            type: Number,
            default: 0,
        },
        appearance: {
            type: Object,
            default: () => ({}),
        },
        accounts: {
            type: Array,
            default: () => [],
        },
        urlName: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            isSheetOpen: false,
            originalBodyOverflow: "",
        };
    },
    computed: {
        visibleAccounts() {
            return [...this.accounts]
                .filter((account) => Number(account?.follower_count || 0) > 0)
                .sort(
                    (a, b) =>
                        Number(b.follower_count || 0) -
                        Number(a.follower_count || 0)
                );
        },
        sheetStyle() {
            const style = {
                color: this.appearance?.fonts_color,
                fontFamily: this.appearance?.fonts,
            };

            if (this.appearance?.type === "solid") {
                style.background = this.appearance?.background || "#fff";
                return style;
            }

            const deviceFolder =
                typeof window !== "undefined" && window.innerWidth <= 576
                    ? "mobile"
                    : "desktop";

            if (this.urlName && this.appearance?.background) {
                style.backgroundImage = `url(${this.urlName}appearance/advance_background/${deviceFolder}/${this.appearance.background})`;
                style.backgroundSize = "cover";
                style.backgroundPosition = "center";
            } else {
                style.background = this.appearance?.background || "#fff";
            }

            return style;
        },
    },
    watch: {
        isSheetOpen(open) {
            if (typeof document === "undefined") {
                return;
            }

            if (open) {
                this.originalBodyOverflow = document.body.style.overflow;
                document.body.style.overflow = "hidden";
                return;
            }

            document.body.style.overflow = this.originalBodyOverflow;
        },
    },
    mounted() {
        window.addEventListener("keydown", this.handleKeydown);
    },
    beforeUnmount() {
        window.removeEventListener("keydown", this.handleKeydown);
        if (this.isSheetOpen && typeof document !== "undefined") {
            document.body.style.overflow = this.originalBodyOverflow;
        }
    },
    methods: {
        formatNumber(value) {
            return Number(value || 0).toLocaleString();
        },
        formatCompactNumber(value) {
            const num = Number(value || 0);
            const formatCompact = (divisor, suffix) =>
                Number((num / divisor).toFixed(1)).toLocaleString() + suffix;

            if (num >= 1000000) {
                return formatCompact(1000000, "m");
            }
            if (num >= 1000) {
                return formatCompact(1000, "k");
            }
            return num.toLocaleString();
        },
        platformIcon(platform) {
            return getSvgForPlatform(platform === "google" ? "youtube" : platform);
        },
        openSheet() {
            if (!this.visibleAccounts.length) {
                return;
            }

            this.isSheetOpen = true;
        },
        closeSheet() {
            this.isSheetOpen = false;
        },
        handleKeydown(event) {
            if (event.key === "Escape") {
                this.closeSheet();
            }
        },
    },
};
</script>

<style scoped>
.follower-stats-display {
    width: calc(100% - 2rem);
    max-width: 22rem;
    margin: 1.15rem auto 0;
    font-weight: 600;
    line-height: 1.2;
}

.follower-stats-display__total {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.35rem;
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: center;
}

.follower-stats-display__total--clickable {
    cursor: pointer;
}

.follower-stats-display__count {
    font-size: 1rem;
    font-weight: 800;
}

.follower-stats-display__label {
    font-size: 0.95rem;
    opacity: 0.82;
}

.follower-stats-display__accounts {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
}

.follower-stats-display__account {
    display: grid;
    grid-template-columns: 3rem minmax(0, 1fr);
    align-items: center;
    gap: 0.9rem;
    width: 100%;
}

.follower-stats-display__avatar-wrap {
    position: relative;
    width: 3rem;
    height: 3rem;
}

.follower-stats-display__avatar {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    object-fit: cover;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid currentColor;
}

.follower-stats-display__avatar--fallback {
    padding: 0.65rem;
}

.follower-stats-display__badge {
    position: absolute;
    right: -0.1rem;
    bottom: -0.1rem;
    width: 1.15rem;
    height: 1.15rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #fff;
    color: #111;
    padding: 0.18rem;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
}

.follower-stats-display__account-copy {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
}

.follower-stats-display__account-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
    font-size: 0.98rem;
}

.follower-stats-display__account-count {
    font-size: 0.9rem;
    opacity: 0.72;
}

.follower-stats-sheet {
    position: fixed;
    inset: 0;
    z-index: 2147483000;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(0, 0, 0, 0.26);
}

.follower-stats-sheet__panel {
    width: 100%;
    max-width: 30rem;
    max-height: min(82vh, 42rem);
    overflow-y: auto;
    padding: 0.8rem 1.5rem max(1.5rem, env(safe-area-inset-bottom));
    border-radius: 1.5rem 1.5rem 0 0;
    border: 1px solid currentColor;
    border-bottom: 0;
    background-color: #fff;
    background-repeat: no-repeat;
    box-shadow: 0 -1rem 3rem rgba(0, 0, 0, 0.24);
}

.follower-stats-sheet__handle {
    width: 3rem;
    height: 0.32rem;
    margin: 0 auto 1rem;
    border-radius: 999px;
    background: currentColor;
    opacity: 0.35;
}

.follower-stats-sheet__header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 2rem;
    margin-bottom: 1.35rem;
}

.follower-stats-sheet__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
}

.follower-stats-sheet__title span {
    font-weight: 800;
}

.follower-stats-sheet__close {
    position: absolute;
    right: -0.35rem;
    top: 50%;
    transform: translateY(-50%);
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    padding: 0;
    line-height: 0;
}

.follower-stats-sheet-enter-active,
.follower-stats-sheet-leave-active {
    transition: opacity 180ms ease;
}

.follower-stats-sheet-enter-active .follower-stats-sheet__panel,
.follower-stats-sheet-leave-active .follower-stats-sheet__panel {
    transition: transform 220ms ease;
}

.follower-stats-sheet-enter-from,
.follower-stats-sheet-leave-to {
    opacity: 0;
}

.follower-stats-sheet-enter-from .follower-stats-sheet__panel,
.follower-stats-sheet-leave-to .follower-stats-sheet__panel {
    transform: translateY(100%);
}
</style>
